import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import { Suspense, lazy, useEffect, useLayoutEffect, useState } from 'react';
import { NavBar } from './components/NavBar';
import { Footer } from './components/Footer';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import Backdrop from './components/Backdrop';
import { FieldProvider, useField } from './lib/field';
import { settlePageTransition } from './lib/pageTransition';

// Only the home page ships in the first bundle. The other pages load in the
// background once the field has drawn (see Preload), so a page change never
// waits on the network. The transition holds its "after" snapshot until a
// route commits, so a chunk still loading never shows as a blank page.
const pages = {
  work: () => import('./pages/Work'),
  media: () => import('./pages/Media'),
  project: () => import('./pages/Project'),
  blog: () => import('./pages/Blog'),
  post: () => import('./pages/BlogPost'),
  about: () => import('./components/About').then((m) => ({ default: m.About })),
};
const Work = lazy(pages.work);
const Media = lazy(pages.media);
const Project = lazy(pages.project);
const Blog = lazy(pages.blog);
const BlogPost = lazy(pages.post);
const About = lazy(pages.about);
const LabHero = lazy(() => import('./lab/LabHero'));
// The tweak panel: always in development, and anywhere with ?tweak in the URL
// (handy on a Vercel preview, on a phone).
const tweaking =
  process.env.NODE_ENV === 'development' || new URLSearchParams(window.location.search).has('tweak');
const Tweaks = tweaking ? lazy(() => import('./dev/Tweaks')) : null;
// Sits inside the routes' Suspense boundary, so it only commits once the new
// page has: the transition's "after" snapshot is the page, never a gap.
function ScrollToTop() {
  const { pathname } = useLocation();

  // Before paint, so a page transition's "after" snapshot starts at the top.
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    settlePageTransition();
  }, [pathname]);

  return null;
}

// A first visit to home opens on the name alone: the nav and the rest wait
// (App.css, html[data-arriving]) until the field has drawn, or 3.5s at most.
function Arrival() {
  const { lit } = useField();
  const [waiting, setWaiting] = useState(() => window.location.pathname === '/');
  useEffect(() => {
    const late = window.setTimeout(() => setWaiting(false), 3500);
    return () => window.clearTimeout(late);
  }, []);
  const arriving = waiting && !lit;
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (arriving) root.dataset.arriving = '';
    else delete root.dataset.arriving;
  }, [arriving]);
  return null;
}

// Fetches the other pages while the visitor is on the first one, after the
// field has drawn so they never compete with it (or after 4s if it can't).
function Preload() {
  const { lit } = useField();
  const [late, setLate] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setLate(true), 4000);
    return () => window.clearTimeout(timer);
  }, []);
  const ready = lit || late;
  useEffect(() => {
    if (!ready) return undefined;
    const idle = window.requestIdleCallback || ((fn) => window.setTimeout(fn, 600));
    const cancel = window.cancelIdleCallback || window.clearTimeout;
    const id = idle(() => Object.values(pages).forEach((load) => load().catch(() => {})));
    return () => cancel(id);
  }, [ready]);
  return null;
}

function App() {
  return (
    <FieldProvider>
      <div className="App">
        <Arrival />
        <Preload />
        <Backdrop />
        <NavBar />
        {/* The pages and footer swipe as one piece; the field behind stays put. */}
        <div className="page-stage">
          <Suspense fallback={null}>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/projects" element={<Work />} />
              <Route path="/projects/:slug" element={<Project />} />
              <Route path="/media" element={<Media />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/about" element={<About />} />
              {/* The QR handed out at events points here; what I'm building now heads Work. */}
              <Route path="/now" element={<Navigate to="/projects" replace />} />
              <Route path="/lab/hero" element={<LabHero />} />
            </Routes>
          </Suspense>
          <Footer />
        </div>
        {Tweaks && (
          <Suspense fallback={null}>
            <Tweaks />
          </Suspense>
        )}
      </div>
    </FieldProvider>
  );
}

export default App;
